import { Helmet } from 'react-helmet-async';
import { useLanguage } from '../../context/LanguageContext';

const SITE_URL = 'https://www.nizus.com';

/**
 * SEOHead — reusable component for page-level SEO meta tags.
 *
 * Props:
 * - title: page title (string)
 * - description: page description (string)
 * - keywords: comma-separated keywords (string)
 * - canonical: canonical path, e.g. "/about" (string)
 * - ogImage: OG image URL (string) — defaults to /img/og-nizus-default.jpg
 * - ogType: Open Graph type (string) — defaults to "website"
 * - noindex: if true, adds noindex/nofollow (boolean)
 * - jsonLd: JSON-LD structured data object (object)
 * - article: { publishedTime, modifiedTime, author, section } for blog articles
 */
export default function SEOHead({
    title,
    description,
    keywords,
    canonical,
    ogImage = '/img/og-nizus-default.jpg',
    ogType = 'website',
    noindex = false,
    jsonLd,
    article,
}) {
    const { language } = useLanguage();
    const fullTitle = title ? `${title} | Nizus` : 'Nizus - Formation pilote en ligne';
    const ogImageUrl = ogImage?.startsWith('http') ? ogImage : `${SITE_URL}${ogImage}`;

    // Canonical et hreflang : strip un éventuel préfixe /fr ou /en de `canonical` pour calculer
    // les URLs par langue. La canonical pointe sur la langue ACTIVE, les hreflang listent les 2
    // langues + x-default (FR par défaut, marché Nizus principalement québécois francophone).
    const canonicalPath = canonical ? canonical.replace(/^\/(fr|en)(?=\/|$)/, '') : null;
    const canonicalUrl = canonicalPath ? `${SITE_URL}/${language}${canonicalPath === '/' ? '' : canonicalPath}` : undefined;
    const frUrl = canonicalPath ? `${SITE_URL}/fr${canonicalPath === '/' ? '' : canonicalPath}` : undefined;
    const enUrl = canonicalPath ? `${SITE_URL}/en${canonicalPath === '/' ? '' : canonicalPath}` : undefined;

    return (
        <Helmet>
            <title>{fullTitle}</title>
            {description && <meta name="description" content={description} />}
            {keywords && <meta name="keywords" content={keywords} />}
            {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}

            {noindex && <meta name="robots" content="noindex, nofollow" />}

            {/* Open Graph */}
            <meta property="og:type" content={article ? 'article' : ogType} />
            <meta property="og:title" content={fullTitle} />
            {description && <meta property="og:description" content={description} />}
            <meta property="og:image" content={ogImageUrl} />
            {canonicalUrl && <meta property="og:url" content={canonicalUrl} />}
            <meta property="og:site_name" content="Nizus" />
            <meta property="og:locale" content={language === 'fr' ? 'fr_CA' : 'en_CA'} />

            {article?.publishedTime && <meta property="article:published_time" content={article.publishedTime} />}
            {article?.modifiedTime && <meta property="article:modified_time" content={article.modifiedTime} />}
            {article?.author && <meta property="article:author" content={article.author} />}
            {article?.section && <meta property="article:section" content={article.section} />}

            {/* Twitter Card */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={fullTitle} />
            {description && <meta name="twitter:description" content={description} />}
            <meta name="twitter:image" content={ogImageUrl} />

            {/* Hreflang : URLs valides pour les 2 langues + x-default (FR) */}
            {frUrl && <link rel="alternate" hreflang="fr-ca" href={frUrl} />}
            {enUrl && <link rel="alternate" hreflang="en-ca" href={enUrl} />}
            {frUrl && <link rel="alternate" hreflang="x-default" href={frUrl} />}

            {/* JSON-LD */}
            {jsonLd && (
                <script type="application/ld+json">
                    {JSON.stringify(jsonLd)}
                </script>
            )}
        </Helmet>
    );
}
